import { dependencies } from '@pn/core/dependencies';
import type { Mapping } from '@pn/core/domain/data';
import {
  getBeforeItemLayerId,
  isDynamicWorkspaceItem,
  isEmptyWorkspaceItem,
  isStreamableItem,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { handleError } from '@pn/core/errors/handleError';
import { dynamicDataProvider } from '@pn/core/services/data/dynamicDataProvider';
import { workspaceActions } from '@pn/core/storage';
import { isWorkspaceItemCleared } from '@pn/core/storage/workspace/workspaceStorage';
import { getWorkspaceSnapshot } from '@pn/core/storage/workspace/workspaceStorageExtras';
import { createConcurrencyGuard } from '@pn/core/utils/concurrency';
import { log } from '@pn/core/utils/debug';
import { notificationService } from '@pn/services/notifications';
import { processLayers, syncVectorFeaturesState } from './layersProcessing';
import { getMapData, type MapData } from './mapData';
import { getTableData, type TableData } from './tableData';

export type Output = {
  tableData: TableData;
  mapData: MapData;
  mapping: Mapping;
};

export const concurrencyGuard = createConcurrencyGuard<{
  autoTrigger: boolean;
}>();

export async function visualizeLoadedWorkspaceItem(params: {
  item: WorkspaceItem;
  pageSize: number;
  extraOptions: {
    autoTrigger?: boolean;
    tableOnly?: boolean;
    silent?: boolean;
    rethrow?: boolean;
  };
}): Promise<Output> {
  const { map } = dependencies;

  const {
    item,
    pageSize,
    extraOptions: {
      autoTrigger = false,
      tableOnly = false,
      silent = false,
      rethrow = false,
    },
  } = params;

  const guard = concurrencyGuard.get(item.id);
  if (guard?.isRunning) {
    if (autoTrigger && !guard?.autoTrigger) {
      log(item.id).info('⛔ skipping auto-triggered routine', guard); // this process is aborted before it runs
      return exitEarly(item.id, true);
    } else {
      log(item.id).info('⚠️ concurrency guard triggered', guard); // previous process will be discarded
      log(item.id).timeEnd('visualizeWorkpaceItem');
      log(item.id).flush();
    }
  }

  const currentGuardIndex = concurrencyGuard.start(item.id, { autoTrigger });

  log().timeLog('map click', `visualizing ${item.id}`);

  log(item.id).time('visualizeWorkpaceItem');

  if (
    item.itemType === 'drawing' ||
    item.id === '_background' ||
    item.id === 'grids' // Grids source layer is non-interactable
  ) {
    return exitEarly(item.id);
  }

  if (item.itemType === 'annotation') {
    log(item.id).info('[special] visualizing annotation');

    const beforeLayerId = findBeforeLayerId(item.id);
    map.addAnnotationLayer(item, beforeLayerId);

    return exitEarly(item.id);
  }

  if (isEmptyWorkspaceItem(item)) {
    log(item.id).info('[special] skipping empty workspace item');

    notificationService.warning('Unable to visualize an empty layer');

    return exitEarly(item.id);
  }

  try {
    const mapping = await dynamicDataProvider(item.dataSource).getDataMapping(
      item
    );

    /* Fetch both table and map data in parallel */
    log(item.id).time('table & map data retrieval');
    const [tableData, mapData] = await Promise.all([
      getTableData({ item, mapping, pageSize, rethrow }),
      getMapData({ item, mapping, silent, rethrow }),
    ]);

    if (concurrencyGuard.isOutdated(item.id, currentGuardIndex)) {
      return exitEarly(item.id, true);
    }

    log(item.id).timeEnd('table & map data retrieval');
    log(item.id).info('tableData', tableData, '\nmapData', mapData);

    if (!tableOnly && !isWorkspaceItemCleared(item.id)) {
      const beforeLayerId = findBeforeLayerId(item.id);
      processLayers({ item, beforeLayerId, mapDataItems: mapData.data });

      if (isStreamableItem(item)) {
        syncVectorFeaturesState(item, mapData.data);
      }
    }

    /* Put table and map data into the storage */
    workspaceActions().markAsRendered(item.id);
    workspaceActions().updateCounts(item.id, {
      totalCount: tableData.totalCount,
      streamedCount: mapData.data.length,
    });
    if (isDynamicWorkspaceItem(item)) {
      workspaceActions().updateNumberOfElements(item.id, tableData.totalCount);
    }
    workspaceActions().updateTableDataItems(item.id, tableData.data);

    log().timeEnd('map click');

    log(item.id).timeEnd('visualizeWorkpaceItem');
    log(item.id).flush();

    concurrencyGuard.stop(item.id);

    return { tableData, mapData, mapping };
  } catch (error) {
    if (rethrow) throw error;
    handleError({
      error,
      notify: `Failed to visualize ${item.name}`,
    });

    return exitWithError(item.id, error);
  }
}

function exitEarly(id: WorkspaceItem['id'], guardTriggered = false): Output {
  if (!guardTriggered) {
    workspaceActions().markAsProcessed(id);
    workspaceActions().markAsRendered(id);
    workspaceActions().updateCounts(id, { totalCount: 0, streamedCount: 0 });
    workspaceActions().updateTableDataItems(id, []);

    log(id).timeEnd('visualizeWorkpaceItem');
    log(id).flush();
  } else {
    log(id).info('⏩ exiting early due to concurrency guard');
  }

  concurrencyGuard.stop(id);

  return {
    tableData: { data: [], totalCount: 0, timestamp: 0 },
    mapData: { data: [], timestamp: 0 },
    mapping: {},
  };
}

function exitWithError(id: WorkspaceItem['id'], error: unknown): Output {
  workspaceActions().markAsError(
    id,
    error instanceof Error ? error.message : String(error)
  );
  workspaceActions().markAsRendered(id); // otherwise it shows as loading instead of the error
  workspaceActions().updateCounts(id, { totalCount: 0, streamedCount: 0 });
  workspaceActions().updateTableDataItems(id, []);

  log(id).timeEnd('visualizeWorkpaceItem');
  log(id).flush();

  concurrencyGuard.stop(id);

  return {
    tableData: { data: [], totalCount: 0, timestamp: 0 },
    mapData: { data: [], timestamp: 0 },
    mapping: {},
  };
}

/**
 * Finds the layer to insert the item's layers under: the nearest visible item
 * above it in the workspace that is actually on the map. Reads the workspace
 * from the store at call time, since it may be reordered while loading.
 */
function findBeforeLayerId(itemId: WorkspaceItem['id']): string | undefined {
  const { map } = dependencies;

  const items = Object.values(getWorkspaceSnapshot().workspace);

  const index = items.findIndex((item) => item.id === itemId);
  if (index === -1) return undefined;

  return items
    .slice(index + 1)
    .filter((item) => item.isVisible)
    .map((item) => getBeforeItemLayerId(item))
    .find((layerId) => map.hasLayer(layerId));
}
